// webapp/src/lib/conventionCrewMessages.js

/**
 * Pure helpers for the convention crew-message channel (sekretariat ⇔
 * standplass). Every function treats its inputs as read-only and returns new
 * arrays/objects — the message list lives in a shared MobX store.
 */

function ts(value) {
    const t = new Date(value).getTime();
    return Number.isFinite(t) ? t : 0;
}

export function lastSeenKey(conventionId, stand) {
    return `crewMessagesSeen-${conventionId}-${parseInt(stand, 10) || 0}`;
}

export function getLastSeen(conventionId, stand) {
    try {
        return parseInt(window.localStorage.getItem(lastSeenKey(conventionId, stand)), 10) || 0;
    } catch (e) {
        return 0;
    }
}

export function setLastSeen(conventionId, stand, tsValue = Date.now()) {
    try {
        window.localStorage.setItem(lastSeenKey(conventionId, stand), String(tsValue));
    } catch (e) {
        // localStorage unavailable (private mode) — unread badge degrades gracefully.
    }
}

export function visibleMessagesForStand(messages = [], stand) {
    const standNum = parseInt(stand, 10) || 0;
    if (standNum < 1) {
        return [...messages];
    }
    return messages.filter(m => m
        && (m.target === 'all'
            || (m.target === 'stand' && parseInt(m.stand, 10) === standNum)
            || (m.fromRole === 'standplass' && parseInt(m.fromStand, 10) === standNum)));
}

export function unreadCount(messages = [], { lastSeenTs = 0, stand = 0, userId = 0 } = {}) {
    return visibleMessagesForStand(messages, stand)
        .filter(m => m
            && parseInt(m.user, 10) !== (parseInt(userId, 10) || -1)
            && ts(m.createdDate) > lastSeenTs)
        .length;
}

export function ackSummary(message = {}, standCount = 0) {
    const relevant = message.target === 'stand'
        ? [parseInt(message.stand, 10)].filter(n => n >= 1)
        : Array.from({ length: Math.max(0, parseInt(standCount, 10) || 0) }, (unused, i) => i + 1);
    const ackedSet = new Set((message.acks || [])
        .map(a => parseInt(a && a.stand, 10))
        .filter(n => n >= 1));
    return {
        acked: relevant.filter(n => ackedSet.has(n)),
        missing: relevant.filter(n => !ackedSet.has(n)),
    };
}

export function pendingViktig(messages = [], stand) {
    const standNum = parseInt(stand, 10) || 0;
    if (standNum < 1) {
        return [];
    }
    return messages
        .filter(m => m
            && parseInt(m.priority, 10) === 1
            && m.fromRole === 'sekretariat'
            && (m.target === 'all' || (m.target === 'stand' && parseInt(m.stand, 10) === standNum))
            && !(m.acks || []).some(a => parseInt(a && a.stand, 10) === standNum))
        .sort((a, b) => ts(a.createdDate) - ts(b.createdDate));
}

export function mergeMessages(existing = [], incoming = []) {
    const byUuid = new Map();
    existing.forEach(m => { if (m && m.uuidv4) byUuid.set(m.uuidv4, m); });
    incoming.forEach(m => { if (m && m.uuidv4) byUuid.set(m.uuidv4, m); });
    return [...byUuid.values()]
        .sort((a, b) => (ts(a.createdDate) - ts(b.createdDate)) || ((a.id || 0) - (b.id || 0)));
}

export function maxUpdatedTs(messages = []) {
    return messages.reduce((max, m) => Math.max(max, ts(m && m.updatedDate)), 0);
}
